/**
 * Shared imports barrel for standalone components
 * This replaces the old SharedModule pattern for convenience in standalone components
 */

// Angular Core Modules
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

// Angular Material Modules
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';

// Custom Pipes
import { PairsPipe } from './pipes/pairs.pipe';
import { ToArabicPipe } from './pipes/toArabic.pipe';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { MatCommonModule } from '@angular/material/core';
import { MatNativeDateModule } from '@angular/material/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormFooterComponent } from '@app/shared/components/form-footer/form-footer.component';
import { CustomFormFooterComponent } from '@app/shared/components/custom-form-footer/custom-form-footer.component';
import { CoreFormContentComponent } from '@app/shared/components/core-form-content/core-form-content.component';
import { NewBreadcrumbComponent } from '@app/shared/components/new-breadcrumb/new-breadcrumb.component';

// Export the pipe for direct imports
export { PairsPipe, ToArabicPipe };

// Third-party Modules
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgOptionHighlightDirective } from '@ng-select/ng-option-highlight';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar-portable';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
// PrimeNG Modules

// Shared Components (only standalone components)

// Note: Directives and pipes are not included in SHARED_IMPORTS as they are not standalone
// They should be imported via NgModules or converted to standalone

/**
 * Common Angular and Material modules used across the app
 * Use this array in standalone component imports for convenience
 */
export const SHARED_IMPORTS = [
  // Angular Core
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  RouterModule,

  // Angular Material
  MatAutocompleteModule,
  MatBadgeModule,
  MatButtonModule,
  MatButtonToggleModule,
  MatCardModule,
  MatCheckboxModule,
  MatChipsModule,
  MatDatepickerModule,
  MatDialogModule,
  MatDividerModule,
  MatExpansionModule,
  MatFormFieldModule,
  MatGridListModule,
  MatIconModule,
  MatInputModule,
  MatListModule,
  MatMenuModule,
  MatPaginatorModule,
  MatProgressBarModule,
  MatProgressSpinnerModule,
  MatRadioModule,
  MatSelectModule,
  MatSidenavModule,
  MatSlideToggleModule,
  MatSliderModule,
  MatSnackBarModule,
  MatSortModule,
  MatStepperModule,
  MatTableModule,
  MatTabsModule,
  MatToolbarModule,
  MatTooltipModule,
  MatTreeModule,
  MatCommonModule,
  MatNativeDateModule,
  FlexLayoutModule,
  // Third-party
  TranslateModule,
  NgSelectModule,
  NgOptionHighlightDirective,
  PerfectScrollbarModule,

  // Custom Pipes
  PairsPipe,
  ToArabicPipe,

  // Shared Components (only standalone components)
  FormFooterComponent,
  CustomFormFooterComponent,
  CoreFormContentComponent,
  NewBreadcrumbComponent
];

/**
 * Minimal set of commonly used imports for basic standalone components
 * Use this for components that only need basic Angular functionality
 */
export const BASIC_IMPORTS = [
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  RouterModule,
  TranslateModule,
  MatButtonModule,
  MatIconModule,
  MatFormFieldModule,
  MatInputModule,
  MatCardModule,
  MatDialogModule,
  MatSnackBarModule,
  FlexLayoutModule
] as const;

/**
 * Material-only imports for components that only need Material UI
 */

export const MATERIAL_IMPORTS = [
  MatAutocompleteModule,
  MatBadgeModule,
  MatButtonModule,
  MatButtonToggleModule,
  MatCardModule,
  MatCheckboxModule,
  MatChipsModule,
  MatDatepickerModule,
  MatDialogModule,
  MatDividerModule,
  MatExpansionModule,
  MatFormFieldModule,
  MatGridListModule,
  MatIconModule,
  MatInputModule,
  MatListModule,
  MatMenuModule,
  MatPaginatorModule,
  MatProgressBarModule,
  MatProgressSpinnerModule,
  MatRadioModule,
  MatSelectModule,
  MatSidenavModule,
  MatSlideToggleModule,
  MatSliderModule,
  MatSnackBarModule,
  MatSortModule,
  MatStepperModule,
  MatTableModule,
  MatTabsModule,
  MatToolbarModule,
  MatTooltipModule,
  MatTreeModule,
  MatCommonModule,
  MatNativeDateModule,
  DragDropModule
] as const;

/**
 * Essential Material modules for basic UI components
 * Use this for simple components that only need basic Material UI
 */
export const MATERIAL_ESSENTIAL_IMPORTS = [
  MatButtonModule,
  MatIconModule,
  MatCardModule,
  MatFormFieldModule,
  MatInputModule,
  MatDialogModule,
  MatSnackBarModule,
  MatProgressSpinnerModule,
  MatTooltipModule
] as const;

/**
 * Material modules for form components
 * Use this for components that handle forms and user input
 */
export const MATERIAL_FORM_IMPORTS = [
  MatFormFieldModule,
  MatInputModule,
  MatSelectModule,
  MatCheckboxModule,
  MatRadioModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatSlideToggleModule,
  MatSliderModule,
  MatChipsModule,
  MatAutocompleteModule
] as const;

/**
 * Material modules for data display components
 * Use this for components that display lists, tables, and data
 */
export const MATERIAL_DATA_IMPORTS = [
  MatTableModule,
  MatPaginatorModule,
  MatSortModule,
  MatListModule,
  MatCardModule,
  MatExpansionModule,
  MatTabsModule,
  MatStepperModule,
  MatTreeModule
] as const;

/**
 * Material modules for navigation components
 * Use this for components that handle navigation and layout
 */
export const MATERIAL_NAV_IMPORTS = [
  MatSidenavModule,
  MatToolbarModule,
  MatMenuModule,
  MatTabsModule,
  MatStepperModule,

  // Custom Pipes
  PairsPipe
] as const;

export const BASIC_FORM_IMPORTS = [];
